<script setup lang="ts">
import { ref, watch, computed, reactive } from 'vue'
import { useAssetSettings, useAssetSettingsLookups } from '@/composables/assets/assets'
import { faEdit } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Card from '@/components/Card/card-container.vue'
import CardContent from '@/components/Card/card-content.vue'
import CardHeader from '@/components/Card/card-header.vue'
import CardHeaderItem from '@/components/Card/card-header-item.vue'
import InputDate from '@/components/input-date.vue'
import InputNumber from '@/components/input-number.vue'
import InputSelect from '@/components/input-select.vue'
import InputLabel from '@/components/input-label.vue'
import InputText from '@/components/input-text.vue'
import InputToggleGroup from '@/components/input-toggle-group.vue'
import GridContainer from '@/components/grid-container.vue'
import GridItem from '@/components/grid-item.vue'
import ContentLoader from '@/components/content-loader.vue'
import { useSetFieldValue, defineRule } from 'vee-validate'
import { AssetType, AssetEscalationType, assetTypeDisplayNames } from '@/features/assets/constants'
import IconHelp from '@/shared/components/IconHelp.vue'
import {
  buildMonthRangeForSelect,
  buildYearRangeForSelect,
  transformToSelectOptions
} from '@/shared/utilities'
import AssetSettingsSpecialConditionsModal from '@/features/assets/ui/components/AssetSettingsSpecialConditionsModal.vue'
import { useModalState } from '@/shared/composables/useModalState'
import { createReusableTemplate } from '@vueuse/core'

const props = defineProps<{ assetCode: string }>()

const specialConditions = ref<string | undefined>()
const { data: assetData, isLoading: asseetLoading } = useAssetSettings(props.assetCode)
const { data: lookups, isLoading: lookupsLoading } = useAssetSettingsLookups()
const contractLength = ref<string>()
const firstEscalationMonth = ref<number>()
const firstEscalationYear = ref<number>()
const isLoading = computed(() => asseetLoading.value || lookupsLoading.value)

const annualEscalationTypeItems = [
  { text: 'Fixed', value: AssetEscalationType.Fixed },
  { text: 'CPI Linked', value: AssetEscalationType.CpiLinked },
  { text: 'Utility Linked', value: AssetEscalationType.UtilityLinked }
]

const annualEscalationTypeSelected = computed(() => {
  const direct = assetData.value?.contractTerms?.annualEscalationType
  if (direct) return direct

  const id = assetData.value?.contractTerms?.annualEscalation?.id
  switch (id) {
    case 1:
      return AssetEscalationType.Fixed
    case 2:
      return AssetEscalationType.CpiLinked
    case 3:
      return AssetEscalationType.UtilityLinked
    default:
      return undefined
  }
})

const convertContractLength = (contractLengthInMonths: number) => {
  if (contractLengthInMonths === 12) return '1 year'

  const years = Math.floor(contractLengthInMonths / 12)
  const months = contractLengthInMonths % 12
  const yearsPart = years > 0 ? `${years} year${years > 1 ? 's' : ''}` : ''
  const monthsPart = months > 0 ? `${months} month${months > 1 ? 's' : ''}` : ''

  return [yearsPart, monthsPart].filter(Boolean).join(' ').trim()
}
watch(
  assetData,
  newAssetData => {
    if (newAssetData === undefined) {
      return
    }
    if (!contractLength.value) {
      contractLength.value = convertContractLength(
        newAssetData.contractTerms?.contractLengthInMonths ?? 0
      )
    }
    firstEscalationMonth.value =
      new Date(newAssetData.billingSettings?.firstEscalationMonth ?? '').getMonth() + 1
    firstEscalationYear.value = new Date(
      newAssetData.billingSettings?.firstEscalationMonth ?? ''
    ).getFullYear()
    specialConditions.value = newAssetData.contractTerms?.specialConditions ?? undefined
  },
  { immediate: true }
)

watch(
  [firstEscalationMonth, firstEscalationYear],
  ([newMonth, newYear]) => {
    if (newMonth !== undefined && newYear !== undefined && assetData.value) {
      assetData.value.contractTerms.firstEscalationMonth = new Date(newYear, newMonth - 1, 1)
    }
  },
  { immediate: true }
)

const specialConditionsModal = reactive(useModalState())
const setSpecialConditions = useSetFieldValue('contractTerms.specialConditions')

defineRule('after_or_equal', (value: Date, compareDate: Date) => {
  if (!value || !compareDate) return true
  return (
    new Date(value) >= new Date(compareDate) ||
    'Practical Completion date cannot be before Contract Effective Date'
  )
})

watch(specialConditions, value => setSpecialConditions(value), { immediate: true })

type FieldProps<T> = {
  name: string
  label: string
  value?: T | null
  tooltip?: string
  required?: boolean
  appliesTo?: AssetType
}
const [DefineDateField, DateField] = createReusableTemplate<
  FieldProps<Date> & { min?: string | Date; max?: string | Date; rules?: string }
>()
const [DefineNumberField, NumberField] = createReusableTemplate<
  FieldProps<number> & { inputSuffix?: string; suffix?: string; inputPrefix?: string }
>()
const [DefineSelectField, SelectField] = createReusableTemplate<
  FieldProps<number> & { items: { text: string; value: any }[] }
>()
const [DefineBooleanField, BooleanField] = createReusableTemplate<FieldProps<boolean>>()

function shouldShowField(type: FieldProps<any>['appliesTo']) {
  return assetData.value?.assetType === undefined
    ? [AssetType.Solar, undefined].includes(type)
    : type === undefined || type === assetData.value.assetType
}
</script>

<template>
  <DefineDateField v-slot="{ name, value, label, tooltip, required, min, max, rules, appliesTo }">
    <template v-if="shouldShowField(appliesTo)">
      <GridItem>
        <InputLabel>{{ label }}</InputLabel>
        <IconHelp v-if="tooltip" class="ms-1" :tooltip="tooltip" />
      </GridItem>
      <GridItem :span="2">
        <InputDate
          placeholder="Date"
          :value="value"
          :name="name"
          :min="min"
          :max="max"
          :rule="rules"
          :required="required"
          error-variant="tooltip" />
      </GridItem>
    </template>
  </DefineDateField>

  <DefineNumberField
    v-slot="{ name, value, label, tooltip, suffix, inputSuffix, inputPrefix, appliesTo }">
    <template v-if="shouldShowField(appliesTo)">
      <GridItem>
        <InputLabel>{{ label }}</InputLabel>
        <IconHelp v-if="tooltip" class="ms-1" :tooltip="tooltip" />
      </GridItem>
      <GridItem :span="typeof suffix === 'string' ? undefined : 2">
        <InputNumber :name="name" error-variant="tooltip" :value="value">
          <template v-if="inputPrefix" #prefix>
            <span class="text-muted">{{ inputPrefix }}</span>
          </template>
          <template v-if="inputSuffix" #suffix>
            <span class="text-muted">{{ inputSuffix }}</span>
          </template>
        </InputNumber>
      </GridItem>
      <GridItem v-if="typeof suffix === 'string'" class="text-muted">
        {{ suffix }}
      </GridItem>
    </template>
  </DefineNumberField>

  <DefineSelectField v-slot="{ name, value, label, tooltip, items, appliesTo }">
    <template v-if="shouldShowField(appliesTo)">
      <GridItem>
        <InputLabel>{{ label }}</InputLabel>
        <IconHelp v-if="tooltip" class="ms-1" :tooltip="tooltip" />
      </GridItem>
      <GridItem :span="2">
        <InputSelect
          placeholder="Please select"
          item-text="text"
          item-value="value"
          :items="items"
          :selected="value"
          :name="name"
          error-variant="tooltip" />
      </GridItem>
    </template>
  </DefineSelectField>

  <DefineBooleanField v-slot="{ name, label, value, appliesTo }">
    <template v-if="shouldShowField(appliesTo)">
      <GridItem>
        <InputLabel>{{ label }}</InputLabel>
      </GridItem>
      <GridItem :span="2">
        <InputToggleGroup
          :name="name"
          :options="[
            { text: 'Yes', value: true },
            { text: 'No', value: false }
          ]"
          :selected="value" />
      </GridItem>
    </template>
  </DefineBooleanField>

  <AssetSettingsSpecialConditionsModal
    v-if="specialConditionsModal.isOpen"
    v-model:value="specialConditions"
    @close="specialConditionsModal.close" />
  <Card class="contract-terms">
    <CardHeader>
      <CardHeaderItem>Contract Terms</CardHeaderItem>
      <IconHelp
        class="ms-1"
        :tooltip="`Access the Review Deal\ntab to create new Annual\nEscalation & Contract\nLength dropdown options\n(requires special access).`" />
    </CardHeader>
    <CardContent>
      <ContentLoader :loading="isLoading" variant="overlay">
        <GridContainer :columns="[355, 55, 'auto']" align="start" :row-gap="10">
          <GridItem>
            <GridContainer :columns="[171, 82, 'auto']" align="center" :row-gap="10">
              <InputLabel>Product Type</InputLabel>
              <GridItem :span="2">
                {{ assetData?.assetType ? assetTypeDisplayNames[assetData.assetType] : '-' }}
              </GridItem>

              <GridItem>
                <InputLabel>Annual Escalation</InputLabel>
              </GridItem>
              <GridItem :span="2">
                <div class="d-grid" style="grid-template-columns: 1fr 92px; gap: 13px">
                  <InputSelect
                    placeholder="Please select"
                    item-text="text"
                    item-value="value"
                    :items="annualEscalationTypeItems"
                    :selected="annualEscalationTypeSelected"
                    name="contractTerms.annualEscalationType"
                    error-variant="tooltip" />

                  <InputNumber
                    name="contractTerms.annualEscalationPercentage"
                    placeholder="0.00"
                    :value="assetData?.contractTerms?.annualEscalationPercentage ?? null"
                    error-variant="tooltip">
                    <template #suffix>
                      <span class="text-muted">%</span>
                    </template>
                  </InputNumber>
                </div>
              </GridItem>

              <SelectField
                name="contractTerms.contractTypeId"
                label="Contract Type"
                :value="assetData?.contractTerms?.contractTypeId ?? undefined"
                :items="transformToSelectOptions(lookups?.contractTypes, 'id', 'name')" />

              <template v-if="[AssetType.Solar, undefined].includes(assetData?.assetType)">
                <GridItem>
                  <InputLabel>Contract Length</InputLabel>
                </GridItem>
                <GridItem :span="2">
                  <span v-if="!contractLength" class="text-muted">Will display here</span>
                  <span v-else>{{ contractLength }}</span>
                </GridItem>
              </template>

              <SelectField
                name="contractTerms.portfolioTypeId"
                label="Portfolio Type"
                :value="assetData?.contractTerms?.portfolioTypeId"
                :items="transformToSelectOptions(lookups?.portfolioTypes, 'id', 'name')" />

              <NumberField
                name="contractTerms.portfolioWeight"
                label="Portfolio Weight"
                :value="assetData?.contractTerms?.portfolioWeight"
                input-suffix="%"
                suffix="" />

              <DateField
                name="contractTerms.fundTransferDate"
                label="Fund Transfer Date"
                :required="true"
                :tooltip="`The date on which the asset\nhas been transferred to a\nseparate special-purpose\nvehicle or fund (if applicable).`"
                :value="assetData?.billingSettings?.fundTransferDate" />

              <DateField
                name="contractTerms.contractEffectiveDate"
                label="Achieved Contract Effective Date"
                :required="true"
                :max="new Date()"
                :tooltip="`The date from which\nthe contract or the\nagreement term starts.`"
                :value="assetData?.billingSettings?.contractEffectiveDate" />

              <GridItem>
                <InputLabel>First Escalation Month</InputLabel>
                <IconHelp
                  class="ms-1"
                  :tooltip="`Customer charges will\nincrease annually, starting\nin this month on the 1st.`" />
              </GridItem>
              <GridItem :span="2">
                <div class="d-grid" style="grid-template-columns: 101px 1fr; gap: 20px">
                  <InputSelect
                    placeholder="Select"
                    item-text="text"
                    item-value="value"
                    :items="buildMonthRangeForSelect()"
                    v-model:selected="firstEscalationMonth"
                    error-variant="tooltip"
                    name="contractTerms.firstEscalationMonth" />
                  <InputSelect
                    placeholder="Select"
                    item-text="text"
                    item-value="value"
                    :items="buildYearRangeForSelect()"
                    error-variant="tooltip"
                    v-model:selected="firstEscalationYear"
                    name="contractTerms.firstEscalationYear" />
                </div>
              </GridItem>

              <NumberField
                name="contractTerms.predictedSellingPrice"
                label="Predicted Selling Price to Fund"
                :value="assetData?.contractTerms?.predictedSellingPrice"
                input-prefix="R" />

              <NumberField
                name="contractTerms.finalSellingPrice"
                label="Final Selling Price to Fund"
                :value="assetData?.contractTerms?.finalSellingPrice"
                input-prefix="R" />

              <SelectField
                name="contractTerms.environmentalAttributeOwnershipId"
                label="Environmental Attribute Ownership"
                :value="assetData?.contractTerms?.environmentalAttributeOwnershipId"
                :items="
                  transformToSelectOptions(lookups?.environmentalAttributeOwnerships, 'id', 'name')
                " />

              <NumberField
                name="contractTerms.performanceRatio"
                label="Performance Ratio"
                :value="assetData?.contractTerms?.performanceRatio"
                input-suffix="%"
                suffix="" />

              <NumberField
                name="contractTerms.latePaymentInterest"
                label="Late Payment Interest"
                :value="assetData?.contractTerms?.latePaymentInterest"
                input-suffix="%"
                suffix="" />
            </GridContainer>
          </GridItem>

          <GridItem class="h-100 text-center">
            <div class="vr text-muted h-100"></div>
          </GridItem>

          <GridItem>
            <GridContainer :columns="[171, 82, '1fr']" :align="'center'" :row-gap="10">
              <NumberField
                name="contractTerms.assetDrawerNumber"
                label="Asset Draw No."
                :value="assetData?.contractTerms?.assetDrawerNumber"
                suffix="" />

              <NumberField
                name="contractTerms.expectedIrradiance"
                label="Expected Irradiance"
                :value="assetData?.contractTerms?.expectedIrradiance"
                suffix="kWh/m²" />

              <NumberField
                name="contractTerms.periodMonthsWAAE"
                label="Period/Months WAAE"
                :value="assetData?.contractTerms?.periodMonthsWAAE"
                suffix="Mos" />

              <NumberField
                name="contractTerms.amaWarrantyPeriodEscalation"
                :applies-to="AssetType.Solar"
                label="AMA Warranty Period Esc"
                :value="assetData?.contractTerms?.amaWarrantyPeriodEsc"
                input-suffix="%"
                suffix="" />

              <NumberField
                name="contractTerms.amaGuaranteePeriodEscalation"
                :applies-to="AssetType.Solar"
                label="AMA Guarantee Period Esc"
                :value="assetData?.contractTerms?.amaGuaranteePeriodEsc"
                input-suffix="%"
                suffix="" />

              <DateField
                name="contractTerms.practicalCompletionDate"
                :rules="`after_or_equal:${assetData?.billingSettings?.contractEffectiveDate}`"
                :applies-to="AssetType.Solar"
                label="Practical Completion"
                :value="assetData?.contractTerms?.practicalCompletion"
                :min="assetData?.billingSettings?.contractEffectiveDate ?? ''" />

              <NumberField
                name="contractTerms.roofRental"
                :applies-to="AssetType.Solar"
                label="Roof Rental"
                :value="assetData?.contractTerms?.roofRental"
                input-prefix="R" />

              <NumberField
                name="contractTerms.roofRentalEscalation"
                :applies-to="AssetType.Solar"
                label="Roof Rental Escalation"
                :value="assetData?.contractTerms?.roofRentalEsc"
                input-suffix="%"
                suffix="" />

              <NumberField
                name="contractTerms.projectBudget"
                :applies-to="AssetType.Solar"
                label="Project Budget"
                :value="assetData?.contractTerms?.projectBudget"
                input-prefix="R" />

              <NumberField
                name="contractTerms.projectCost"
                :applies-to="AssetType.Solar"
                label="Project Cost"
                :value="assetData?.contractTerms?.projectCost"
                input-prefix="R" />

              <BooleanField
                name="contractTerms.isOwnedByClient"
                :applies-to="AssetType.Solar"
                label="Owned by Client"
                :value="assetData?.contractTerms?.isOwnedByClient" />

              <BooleanField
                name="contractTerms.isMaintenanceSLA"
                :applies-to="AssetType.Solar"
                label="Maintenance SLA"
                :value="assetData?.contractTerms?.isMaintenanceSLA" />

              <DateField
                name="contractTerms.fixedLeaseStartDate"
                :applies-to="AssetType.Solar"
                label="Fixed Lease Start Date"
                :value="assetData?.contractTerms?.fixedLeaseStartDate" />

              <template v-if="[AssetType.Solar, undefined].includes(assetData?.assetType)">
                <GridItem>
                  <InputLabel>Degradation Factor</InputLabel>
                </GridItem>
                <GridItem :span="2">{{
                  lookups?.degradationTypes?.find(
                    x => x.id === assetData?.contractTerms?.degradationType
                  )?.name ?? '-'
                }}</GridItem>
              </template>

              <SelectField
                :applies-to="AssetType.Wheeling"
                name="contractTerms.vppaTypeId"
                label="VPPA Type"
                disabled
                :value="assetData?.contractTerms?.vppaTypeId"
                :items="transformToSelectOptions(lookups?.vppaTypes, 'id', 'name')" />

              <NumberField
                name="contractTerms.vppaSystemSizeSoldInKwp"
                :applies-to="AssetType.Wheeling"
                label="VPPA System Size - Sold"
                :value="assetData?.contractTerms?.vppaSystemSizeSoldInKwp"
                suffix="kWp" />

              <NumberField
                name="contractTerms.vppaSystemSizeFundInKwp"
                :applies-to="AssetType.Wheeling"
                label="VPPA System Size - Fund"
                :value="assetData?.contractTerms?.vppaSystemSizeFundInKwp"
                suffix="kWp" />

              <NumberField
                name="contractTerms.vppaSystemSizeAsBuiltInKwp"
                :applies-to="AssetType.Wheeling"
                label="VPPA System Size - As Built"
                :value="assetData?.contractTerms?.vppaSystemSizeAsBuiltInKwp"
                suffix="kWp" />

              <NumberField
                name="contractTerms.initialContractTermInYears"
                :applies-to="AssetType.Wheeling"
                label="Initial Contract Term"
                :value="assetData?.contractTerms?.initialContractTermInYears"
                suffix="Yrs" />

              <NumberField
                name="contractTerms.fullContractTermInYears"
                :applies-to="AssetType.Wheeling"
                label="Full Contract Term"
                :value="assetData?.contractTerms?.fullContractTermInYears"
                suffix="Yrs" />

              <BooleanField
                name="contractTerms.areRenewableEnergyCreditsLinked"
                :applies-to="AssetType.Wheeling"
                label="RECs Linked?"
                :value="assetData?.contractTerms?.areRenewableEnergyCreditsLinked" />

              <GridItem>
                <InputLabel>Special Conditions</InputLabel>
              </GridItem>
              <GridItem :span="2">
                <span
                  class="d-inline-block"
                  style="cursor: pointer"
                  @click="specialConditionsModal.open">
                  <FontAwesomeIcon :icon="faEdit" />
                </span>
              </GridItem>
              <GridItem span style="margin-top: -7px; max-width: 100%">
                <span v-if="!specialConditions">Will display here.</span>
                <span v-else class="text-truncate d-block" style="min-width: 0">{{
                  specialConditions.substring(0, 512)
                }}</span>
              </GridItem>

              <GridItem>
                <InputLabel class="text-nowrap">Signed Agreement Link</InputLabel>
              </GridItem>
              <GridItem :span="2" />
              <GridItem span style="margin-top: -7px">
                <InputText
                  name="contractTerms.signedAgreementLink"
                  error-variant="tooltip"
                  :value="assetData?.contractTerms.signedAgreementLink" />
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
      </ContentLoader>
    </CardContent>
  </Card>
</template>
<style module lang="scss">
@import '@/assets/variables';

.loader {
  min-height: $input-height;
}

.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 50px - 355px);
}
</style>
<style scoped lang="scss">
.contract-terms {
  min-height: 205px;

  .card-base-grid {
    display: grid;
    align-items: baseline;
    row-gap: 10px;
    grid-template-columns: 122px 1fr;
    column-gap: 20px;
    color: #6d6e71;
  }
}

.first-escalation-wrapper {
  display: grid;
  grid-template-columns: 101px 1fr;
  gap: 20px;
  height: 28px;
}

.medium-input {
  width: 115px;
}

.input-currency {
  :deep(.form-control) {
    text-align: right !important;
  }

  :deep(input) {
    border-left: none;
    text-align: right;
  }

  :deep(.input-group-text) {
    color: #d1d1d1;
    background-color: #fff !important;
  }

  :deep(.input-group) {
    width: unset !important;
  }
}

.input-percentage {
  :deep(.form-control) {
    text-align: left;
  }

  :deep(input) {
    border-right: none;
  }

  :deep(.input-group-text) {
    color: #d1d1d1;
    background-color: #fff !important;
  }
}

.card-input-title {
  font-size: 12px;
  font-weight: bold;
  color: #6d6e71;
}

.tooltip-color {
  color: #d1d1d1;
}
</style>
